class ProductNavigation {
  constructor({
    collectionHandle,
    allImagesCollectionHandle,
    allImagesCollectionCanonicalHandle,
    currentProduct,
    currentProductId,
    paginateBy = 40,
    localePath,
  }) {
    this.collectionHandle = collectionHandle;
    this.allImagesCollectionHandle = allImagesCollectionHandle;
    this.allImagesCollectionCanonicalHandle = allImagesCollectionCanonicalHandle;
    this.currentProduct = (currentProduct || "").trim();
    this.currentProductId = Number(currentProductId) || null;
    this.paginateBy = Number(paginateBy);
    this.localePath = localePath;
    this.currentTag = this.currentTagRaw;
    this.allProducts = [];
    this.filteredProductIds = [];
    this.currentPage = 1;
    this.totalPages = 1;
    // Cache TTL: 24 hours
    this.CACHE_TTL_MS = 24 * 60 * 60 * 1000;
    this.fullCacheKey = `allProducts_${this.collectionHandle}`;
    this.tagCacheKey = this.currentTag
      ? `allProducts_${this.collectionHandle}_${this.slugifyTag(this.currentTag)}`
      : null;
    this.productCollectionMap = new Map();
  }

  slugifyTag(tag) {
    return (tag || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[\s_]+/g, "-")
      .replace(/[^a-z0-9\-]/g, "")
      .replace(/-+/g, "-");
  }

  buildCollectionPageUrl(page) {
    const params = new URLSearchParams(window.location.search);
    params.set("page", page);
    const query = params.toString();
    const base = this.localePath
      ? `${this.localePath}/collections/${this.collectionHandle}`
      : `/collections/${this.collectionHandle}`;
    return query ? `${base}?${query}` : base;
  }

  buildProductUrl(handle, page) {
    const params = new URLSearchParams(window.location.search);
    params.set("page", page);
    const query = params.toString();
    const base = this.localePath
      ? `${this.localePath}/collections/${this.collectionHandle}/products/${handle}`
      : `/collections/${this.collectionHandle}/products/${handle}`;
    return query ? `${base}?${query}` : base;
  }

  loadCache(key, expectProducts = false) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      const age = Date.now() - (obj.updated || 0);
      if (age > this.CACHE_TTL_MS) return null;
      if (expectProducts && Array.isArray(obj.products)) return obj.products;
      if (!expectProducts && Array.isArray(obj.ids)) return obj.ids;
      return null;
    } catch {
      return null;
    }
  }

  saveFullCache() {
    try {
      localStorage.setItem(
        this.fullCacheKey,
        JSON.stringify({ products: this.allProducts, updated: Date.now() })
      );
    } catch {}
  }

  saveTagCache() {
    if (!this.tagCacheKey) return;
    try {
      localStorage.setItem(
        this.tagCacheKey,
        JSON.stringify({
          ids: this.filteredProductIds,
          updated: Date.now(),
          tag: this.currentTag,
        })
      );
    } catch {}
  }

  async fetchPage(page) {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("limit", this.paginateBy);
    const url = `/collections/${this.collectionHandle}/products.json?${params.toString()}`;
    try {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return [];
      const data = await res.json();
      const arr = Array.isArray(data.products) ? data.products : [];
      return arr.map((p) => ({
        id: Number(p.id),
        handle: p.handle,
        tags: Array.isArray(p.tags) ? p.tags : [],
      }));
    } catch {
      return [];
    }
  }

  async buildAllProducts() {
    const cachedFull = this.loadCache(this.fullCacheKey, true);
    if (cachedFull && cachedFull.length) {
      this.allProducts = cachedFull;
      return;
    }
    const collected = [];
    const MAX_PAGES = 500; // fixed upper cap
    for (let page = 1; page <= MAX_PAGES; page++) {
      const list = await this.fetchPage(page);
      if (!list.length) break;
      collected.push(...list);
      if (list.length < this.paginateBy) break; // last page
    }
    this.allProducts = collected;
    if (collected.length) this.saveFullCache();
  }

  applyTagFilter() {
    if (!this.currentTag) {
      this.filteredProductIds = this.allProducts.map((p) => p.id);
      return;
    }
    const cachedTagIds = this.loadCache(this.tagCacheKey);
    if (cachedTagIds && cachedTagIds.length) {
      this.filteredProductIds = cachedTagIds;
      return;
    }
    const tagLower = this.currentTag.toLowerCase();
    this.filteredProductIds = this.allProducts
      .filter((p) => p.tags.some((t) => String(t).toLowerCase() === tagLower))
      .map((p) => p.id);
    this.saveTagCache();
  }

  deriveIndexAndPage() {
    let list = this.filteredProductIds;
    if (!list.length) return null;

    let targetId = this.currentProductId;
    if (!targetId && this.currentProduct && this.allProducts.length) {
      const byHandle = this.allProducts.find(
        (p) => p.handle === this.currentProduct
      );
      if (byHandle) targetId = byHandle.id;
    }

    let idx = list.indexOf(targetId);

    if (idx === -1) {
      // single fallback to full list
      list = this.allProducts.map((p) => p.id);
      this.filteredProductIds = list;
      idx = list.indexOf(targetId);
      if (idx === -1) {
        // Bail out (no match); choose first product or return null
        if (list.length) {
          idx = 0;
          targetId = list[0];
        } else {
          return null;
        }
      }
    }

    this.totalPages = Math.max(
      1,
      Math.ceil(this.filteredProductIds.length / this.paginateBy)
    );
    this.currentPage = Math.floor(idx / this.paginateBy) + 1;
    return idx;
  }

  updateNavigation(idx) {
    const prevEl = document.querySelector(".product-nav__previous");
    const nextEl = document.querySelector(".product-nav__next");
    const list = this.filteredProductIds;
    const prevId = idx > 0 ? list[idx - 1] : null;
    const nextId = idx < list.length - 1 ? list[idx + 1] : null;
    const find = (id) => this.allProducts.find((p) => p.id === id) || null;
    const prevProduct = prevId ? find(prevId) : null;
    const nextProduct = nextId ? find(nextId) : null;
    const prevPage = prevProduct
      ? Math.floor((idx - 1) / this.paginateBy) + 1
      : null;
    const nextPage = nextProduct
      ? Math.floor((idx + 1) / this.paginateBy) + 1
      : null;
    if (prevEl) {
      if (prevProduct?.handle) {
        prevEl.href = this.buildProductUrl(prevProduct.handle, prevPage);
        prevEl.classList.remove("disabled");
      } else prevEl.classList.add("disabled");
    }
    if (nextEl) {
      if (nextProduct?.handle) {
        nextEl.href = this.buildProductUrl(nextProduct.handle, nextPage);
        nextEl.classList.remove("disabled");
      } else nextEl.classList.add("disabled");
    }
  }

  updateCloseLink() {
    const el = document.querySelector(".product-nav__close");
    if (el) el.href = this.buildCollectionPageUrl(this.currentPage);
  }

  get currentTagRaw() {
    const raw = new URLSearchParams(window.location.search).get("tag");
    return raw ? decodeURIComponent(raw) : null;
  }

  get referrerPath() {
    const referrer = document.referrer;
    return referrer ? new URL(referrer).pathname : null;
  }

  get isReferrerCollection() {
    if (!this.referrerPath) return false;
    return this.referrerPath.includes("/collections/");
  }

  get isReferrerProduct() {
    if (!this.referrerPath) return false;
    return this.referrerPath.includes("/products/");
  }

  get referrer() {
    if (!this.referrerPath) return null;
    const parts = this.referrerPath.split("/").filter(Boolean);
    if (this.localePath) {
      const localeParts = this.localePath.split("/").filter(Boolean);
      if (
        localeParts.length &&
        parts[0] === localeParts[localeParts.length - 1]
      )
        parts.shift();
    }
    if (parts.length < 2) return null;
    return parts[1];
  }

  /**
   * Set referrer collection in storage
   */
  setReferrer() {
    if (this.isReferrerCollection) {
      const collection = this.referrer;
      this.productCollectionMap.set(this.currentProduct, collection);
      localStorage.setItem(
        "referrerCollection",
        JSON.stringify(Array.from(this.productCollectionMap.entries()))
      );
    } else if (this.isReferrerProduct) {
        const previousProduct = this.referrer;
        const existingCollection = this.productCollectionMap.get(previousProduct);
        if (existingCollection) {
            this.productCollectionMap.set(this.currentProduct, existingCollection);
            localStorage.setItem(
              "referrerCollection",
              JSON.stringify(Array.from(this.productCollectionMap.entries()))
            );
        }
    }
    this.setCollectionHandle();
  }

  setReferrerCollectionMapFromStorage() {
    this.productCollectionMap = new Map();
    const storedMap = localStorage.getItem('referrerCollection');
    if (storedMap) {
      try {
          this.productCollectionMap = new Map(JSON.parse(storedMap));
      } catch {
          this.productCollectionMap = new Map();
      }
    }
  }

  setCollectionHandle() {
    const referrerCollection = this.productCollectionMap.get(this.currentProduct);
    if (referrerCollection && referrerCollection.includes('all')) {
      this.collectionHandle = this.allImagesCollectionCanonicalHandle;
    }
    this.fullCacheKey = `allProducts_${this.collectionHandle}`;
    this.tagCacheKey = this.currentTag
      ? `allProducts_${this.collectionHandle}_${this.slugifyTag(this.currentTag)}`
      : null;
  }

  addKeyboardListeners() {
    document.addEventListener("keydown", e => {
      if (e.key === "Escape") {
        const close = document.querySelector(".product-nav__close");
        if (close?.href) location.href = close.href;
      } else if (e.key === "ArrowLeft") {
        const prev = document.querySelector(".product-nav__previous");
        if (prev && !prev.classList.contains("disabled")) location.href = prev.href;
      } else if (e.key === "ArrowRight") {
        const next = document.querySelector(".product-nav__next");
        if (next && !next.classList.contains("disabled")) location.href = next.href;
      }
    });
  }

  async init() {
    this.setReferrerCollectionMapFromStorage();
    this.setReferrer();
    await this.buildAllProducts();
    if (!this.allProducts.length) return;
    this.applyTagFilter();
    const idx = this.deriveIndexAndPage();
    if (idx == null) return;
    this.updateNavigation(idx);
    this.updateCloseLink();
    this.addKeyboardListeners();
    // initiate swipe indicator
    new window.SwipeIndicatorTd({
      contentSelector: 'section.product[data-section-type="product"]',
      canSwipeLeft: () => {
        const prev = document.querySelector('.product-nav__next');
        return prev && !prev.classList.contains('disabled');
      },
      onSwipeLeft: () => {
        const next = document.querySelector('.product-nav__next');
        if (next && !next.classList.contains('disabled')) next.click();
      },
      canSwipeRight: () => {
        const next = document.querySelector('.product-nav__previous');
        return next && !next.classList.contains('disabled');
      },
      onSwipeRight: () => {
        const prev = document.querySelector('.product-nav__previous');
        if (prev && !prev.classList.contains('disabled')) prev.click();
      }
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (!window.productNav) return;
  new ProductNavigation(window.productNav).init();
});
